import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { 
  PersonOutline, 
  EmailOutlined, 
  LockOutlined, 
  Business,
  CheckCircle
} from '@mui/icons-material';
import { authService, subscriptionService } from '../services/api';
import { isFreePeriod } from '../config/freeAccess';

const Register = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    agreeToTerms: false
  });
  
  // Selected plan
  const [selectedPlan, setSelectedPlan] = useState('annual');
  
  // Form errors
  const [errors, setErrors] = useState({});
  
  const freePeriod = isFreePeriod();
  const steps = freePeriod
    ? ['Create Account', 'Get Started']
    : ['Create Account', 'Choose Plan', 'Payment'];
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business/Salon name is required';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      if (freePeriod) {
        handleRegisterFree();
      } else {
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      handleRegisterAndCheckout();
    }
  };

  // Free period: register without plan selection or Stripe
  const handleRegisterFree = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.register({
        username: formData.email,
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        business_name: formData.businessName,
        selectedPlan: 'free_period'
      });

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('isAuthenticated', 'true');
      }

      // Full page load so AuthContext picks up the stored token and user
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };
  
  const handleRegisterAndCheckout = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Register the user
      const registerData = {
        username: formData.email, // Use email as username
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        business_name: formData.businessName,
        selectedPlan: selectedPlan
      };
      
      const response = await authService.register(registerData);
      
      if (response.data.access_token) {
        // Store the token
        localStorage.setItem('token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      // Now redirect to Stripe checkout
      setActiveStep(2);
      
      const checkoutResponse = await subscriptionService.checkout(selectedPlan);
      
      const checkoutUrl = 
        checkoutResponse.data?.sessionUrl || 
        checkoutResponse.data?.checkout_url || 
        checkoutResponse.data?.redirect_url ||
        checkoutResponse.data?.url;
      
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        setError('Could not create checkout session. Please try again or contact support.');
        setActiveStep(1);
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      
      let errorMessage = 'Registration failed. Please try again.';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
      setActiveStep(0);
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderStep1 = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Create Your Account
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            name="firstName"
            label="First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            name="lastName"
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            name="email"
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password || 'At least 8 characters'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            name="businessName"
            label="Salon / Business Name"
            value={formData.businessName}
            onChange={handleChange}
            error={!!errors.businessName}
            helperText={errors.businessName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link to="/terms" target="_blank" style={{ color: '#1976d2' }}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" style={{ color: '#1976d2' }}>
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
          {errors.agreeToTerms && (
            <Typography color="error" variant="caption" sx={{ ml: 2 }}>
              {errors.agreeToTerms}
            </Typography>
          )}
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          component={Link}
          to="/login"
          variant="text"
        >
          Already have an account? Sign in
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          size="large"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : freePeriod ? (
            'Create Free Account'
          ) : (
            'Continue to Plans'
          )}
        </Button>
      </Box>
    </Box>
  );
  
  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 1, textAlign: 'center' }}>
        Choose Your Plan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
        Full access to the SalonLogic Pricing Calculator
      </Typography>
      
      <Grid container spacing={3} justifyContent="center">
        {/* Annual Plan */}
        <Grid item xs={12} sm={6}>
          <Card 
            variant="outlined"
            onClick={() => setSelectedPlan('annual')}
            sx={{ 
              cursor: 'pointer',
              borderWidth: 2,
              borderColor: selectedPlan === 'annual' ? 'primary.main' : 'divider',
              backgroundColor: selectedPlan === 'annual' ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Annual Plan
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main', my: 2 }}>
                £97
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                per year
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ textAlign: 'left', mt: 2 }}>
                {['Full calculator access', 'PDF & Excel exports', 'Save your pricing data', 'All future updates', 'Email support'].map((feature, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle color="success" sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>
              
              {selectedPlan === 'annual' && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                  <Typography variant="body2">Selected</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Lifetime Plan */}
        <Grid item xs={12} sm={6}>
          <Card 
            variant="outlined"
            onClick={() => setSelectedPlan('lifetime')}
            sx={{ 
              cursor: 'pointer',
              borderWidth: 2,
              borderColor: selectedPlan === 'lifetime' ? 'success.main' : 'divider',
              backgroundColor: selectedPlan === 'lifetime' ? 'rgba(46, 125, 50, 0.04)' : 'transparent',
              position: 'relative',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'success.main',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {/* Best Value Badge */}
            <Box sx={{ 
              position: 'absolute', 
              top: -12, 
              left: '50%', 
              transform: 'translateX(-50%)',
              bgcolor: 'success.main',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              BEST VALUE
            </Box>
            
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Lifetime Access
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main', my: 2 }}>
                £199.99
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                one-time payment
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ textAlign: 'left', mt: 2 }}>
                {['Full calculator access', 'PDF & Excel exports', 'Save your pricing data', 'All future updates', 'Priority support', 'Never pay again'].map((feature, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle color="success" sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2" fontWeight={i === 5 ? 'bold' : 'normal'}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
              
              {selectedPlan === 'lifetime' ? (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'success.main', color: 'white', borderRadius: 1 }}>
                  <Typography variant="body2">Selected</Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2, p: 1, bgcolor: '#e8f5e9', color: 'success.main', borderRadius: 1 }}>
                  <Typography variant="body2">Save £91+ vs 3 years</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleBack} variant="outlined">
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          size="large"
          color={selectedPlan === 'lifetime' ? 'success' : 'primary'}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `Continue to Payment - ${selectedPlan === 'annual' ? '£97' : '£199.99'}`
          )}
        </Button>
      </Box>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          🔒 Secure payment powered by Stripe
        </Typography>
      </Box>
    </Box>
  );
  
  const renderStep3 = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={48} sx={{ mb: 3 }} />
      <Typography variant="h5" gutterBottom>
        Redirecting to Payment...
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Please wait while we connect you to our secure payment processor.
      </Typography>
    </Box>
  );
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Logo/Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            SalonLogic
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Pricing Calculator
          </Typography>
        </Box>
        
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {/* Step Content */}
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Paper>
      
      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Questions? Contact us at{' '}
          <a href="mailto:andrew@salonlogic.co.uk" style={{ color: '#1976d2' }}>
            andrew@salonlogic.co.uk
          </a>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          © {new Date().getFullYear()} SalonLogic. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default Register;